import { useState, useEffect } from 'react';
import { calculateTaxWithStripe } from '@/utils/taxCalculation';
import { TaxInfo } from './types';

export const useTaxCalculation = (
  subtotal: number,
  discount: number,
  shippingZipCode: string
) => {
  const [taxInfo, setTaxInfo] = useState<TaxInfo>({ 
    taxAmount: 0, 
    taxRate: 0, 
    stateName: '' 
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    
    const calculateTax = async () => {
      const taxAddressStr = sessionStorage.getItem('taxAddress');
      const taxAddress = taxAddressStr ? JSON.parse(taxAddressStr) : null;
      
      console.log('=== TAX CALCULATION DEBUG ===');
      console.log('useTaxCalculation: Starting tax calculation with:', { 
        subtotal, 
        discount, 
        shippingZipCode,
        taxAddress,
        hasZipCode: !!shippingZipCode,
        hasFullAddress: !!taxAddress,
        validSubtotal: subtotal > 0,
        taxAddressRaw: taxAddressStr
      });

      // Only calculate tax if we have a complete address and valid subtotal
      if (!taxAddress || subtotal <= 0 || 
          !taxAddress.address || !taxAddress.city || !taxAddress.state || !taxAddress.postal_code) {
        console.log('useTaxCalculation: Tax calculation skipped - missing complete address or invalid subtotal');
        if (!isCancelled) {
          setTaxInfo({ taxAmount: 0, taxRate: 0, stateName: '' });
        }
        return;
      }

      if (!isCancelled) {
        setIsLoading(true);
      }
      
      try {
        const discountedSubtotal = Math.max(0, subtotal - discount);
        console.log('useTaxCalculation: Calculating tax for:', { 
          subtotal, 
          discountedSubtotal, 
          address: taxAddress 
        });
        
        const result = await calculateTaxWithStripe(discountedSubtotal, taxAddress);
        
        if (!isCancelled) {
          console.log('useTaxCalculation: Tax calculation result:', result);
          setTaxInfo(result);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('useTaxCalculation: Tax calculation failed:', error);
          setTaxInfo({ taxAmount: 0, taxRate: 0, stateName: 'Error' });
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    // Debounced calculation with cleanup
    const timeoutId = setTimeout(() => {
      if (!isCancelled) {
        calculateTax();
      }
    }, 300); // Increased debounce to reduce API calls
    
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [subtotal, discount, shippingZipCode]);

  return { taxInfo, isLoading };
};